import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'

/**
 * Inbjudningssidan: /ga-med/:token
 *
 * Gängets namn hämtas med peek_group så man ser vad man går med i innan man
 * skriver lösenordet. Utan giltig token returnerar den ingenting — sidan
 * avslöjar alltså inte vilka gäng som finns.
 */
export function JoinGroup() {
  const { token = '' } = useParams()
  const navigate = useNavigate()

  const [groupName, setGroupName] = useState<string | null>(null)
  const [memberCount, setMemberCount] = useState<number>(0)
  const [checking, setChecking] = useState(true)
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    supabase
      .rpc('peek_group', { p_invite_token: token })
      .then(({ data, error }) => {
        if (error) setError(error.message)
        else if (Array.isArray(data) && data.length > 0) {
          setGroupName(data[0].name as string)
          setMemberCount(Number(data[0].member_count ?? 0))
        }
        setChecking(false)
      })
  }, [token])

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)

    const { error } = await supabase.rpc('join_group', {
      p_invite_token: token,
      p_password: password,
    })

    setBusy(false)
    if (error) setError(error.message)
    else navigate('/gang', { replace: true })
  }

  if (checking) {
    return (
      <div className="page" style={{ maxWidth: 460 }}>
        <p className="muted">Hämtar inbjudan…</p>
      </div>
    )
  }

  if (!groupName) {
    return (
      <div className="page stack" style={{ maxWidth: 460 }}>
        <h1>Inbjudan</h1>
        <div className="card stack">
          <div className="notice notice--error">
            Länken gäller inte. Den kan ha stängts av, eller så blev den avkortad
            när den kopierades.
          </div>
          <button onClick={() => navigate('/gang')}>Till mina gäng</button>
        </div>
      </div>
    )
  }

  return (
    <div className="page stack" style={{ maxWidth: 460 }}>
      <div>
        <p className="eyebrow">Inbjudan</p>
        <h1>{groupName}</h1>
      </div>

      <form className="card stack" onSubmit={onSubmit}>
        <p className="muted">
          {memberCount === 1 ? '1 medlem' : `${memberCount} medlemmar`} i gänget.
        </p>

        <div>
          <label htmlFor="join-password">Gängets lösenord</label>
          <input
            id="join-password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        {error && <div className="notice notice--error">{error}</div>}

        <button className="primary" type="submit" disabled={busy || !password}>
          {busy ? 'Går med…' : 'Gå med'}
        </button>
      </form>
    </div>
  )
}
